import { formatMoney } from './utils-format-money.js';

/**
 * Kaching total (21 Oct, Kopstorm). On a product page with a Kaching deal, Kaching writes the
 * price of the selected bar into the add to cart button but leaves the theme add-ons out, while
 * the theme adds the add-ons to the single variant price. Both write into the same button, so the
 * total was wrong as soon as a bundle bar and an add-on were combined (GRID Backpack Pro: bar
 * $199 + Compression Closet $53 showed $232). While a Kaching deal is visible for this product,
 * the button now shows the selected bar price plus the ticked Kaching upsells plus the selected
 * theme add-ons, on the main and the sticky button. Without a visible deal (Kaching off, paused,
 * or hidden by a test arm) nothing changes and the variant price is used as before.
 */
const EK_KACHING_BUNDLE = 'kaching-bundle';
const EK_KACHING_BAR = '.kaching-bundles__bar';
const EK_KACHING_UPSELL = '.kaching-bundles__checkbox-upsells__upsell';
const EK_KACHING_SELECTED = '.kaching-bundles__bar--selected';
const EK_KACHING_PRICE = '.kaching-bundles__bar-price';
const EK_KACHING_FULL_PRICE = '.kaching-bundles__bar-full-price';

/**
 * Reads a rendered money string ($199, €179,95, 1.234,56 kr) into cents.
 * @param {string} text
 * @returns {number|null}
 */
const ekMoneyToCents = (text) => {
  const clean = (text || '').replace(/[\s\u00a0'\u2019]/g, '');
  const match = clean.match(/\d[\d.,]*/);
  if (!match) return null;
  const numeric = match[0].replace(/[.,]$/, '');
  const separators = numeric.match(/[.,]/g) || [];
  let decimal = '';
  if (separators.length) {
    const lastSeparator = separators[separators.length - 1];
    const last = numeric.lastIndexOf(lastSeparator);
    const tail = numeric.length - last - 1;
    const mixed = separators.some((separator) => separator !== lastSeparator);
    // 1,234 or 1.234 is a thousands separator; 39,95, 39.5 and 1.234,56 end in a decimal part.
    if (mixed || tail !== 3) decimal = lastSeparator;
  }
  let value;
  if (decimal) {
    const last = numeric.lastIndexOf(decimal);
    value = parseFloat(numeric.slice(0, last).replace(/[.,]/g, '') + '.' + numeric.slice(last + 1));
  } else {
    value = parseFloat(numeric.replace(/[.,]/g, ''));
  }
  return Number.isFinite(value) ? Math.round(value * 100) : null;
};

class ProductPrice extends HTMLElement {
  hasPopularAddOns = this.hasAttribute('data-synced-with-product-add-ons') || false;

  addOnCents = 0;
  addOnCompareCents = 0;

  connectedCallback() {
    this.mainProduct = this.closest('main-product') || document.querySelector('main-product[data-is-main-product]');
    this.sectionId = this.dataset.sectionId || this.mainProduct?.dataset.sectionId;
    this.popularAddOns = this.mainProduct?.getPopularAddOns() || [];

    this.handleAddOnPriceChange();

    this.unsubscribeVariantChange = subscribe(
      `variant:changed:${this.dataset.productId}:${this.sectionId}`,
      ({ html }) => {
        this.handlePriceChange(html);
      }
    );

    // Make sure the product is a add-on
    if (!this.hasPopularAddOns) return;

    this.unsubscribePriceChanged = subscribe(
      `price:changed:product-add-ons:${this.sectionId}`,
      ({ priceInCents, compareAtPriceInCents }) => {
        this.updatePriceWith(priceInCents);
        this.updateCompareAtPriceWith(compareAtPriceInCents);
      }
    );

    this.watchKaching();
  }

  handleAddOnPriceChange() {
    // Check if the product
    if (!this.hasPopularAddOns) return;

    // Update the price with the add-on value
    const addOnValue = parseInt(this.popularAddOns.reduce((total, item) => total + item.price, 0));
    this.updatePriceWith(addOnValue);

    // Update the compare price with the add-on value
    const compareAtPriceValue = parseInt(
      this.popularAddOns.reduce((total, item) => total + (item.compareAtPrice || item.price), 0)
    );
    this.updateCompareAtPriceWith(compareAtPriceValue);
  }

  disconnectedCallback() {
    this.unsubscribeVariantChange();
    if (this.hasPopularAddOns) {
      this.unsubscribePriceChanged();
    }
    clearInterval(this.kachingPoll);
    clearTimeout(this.kachingTimer);
    this.kachingObserver?.disconnect();
    this.selfObserver?.disconnect();
  }

  /**
   * Handle price change.
   * @param {HTMLElement} html - The HTML to render.
   */
  handlePriceChange(html) {
    const productPrice = html?.querySelector('product-price');
    if (!productPrice) return;
    this.innerHTML = productPrice.innerHTML;
    this.applyKachingTotal();
  }

  /**
   * The selected Kaching bar for this product, or null when no deal is visible.
   * @returns {{price: number, compareAtPrice: number}|null}
   */
  getKachingBase() {
    const bundle = this.findKachingBundle();
    if (!bundle || bundle.getClientRects().length === 0) return null;

    // The deal bar. Kaching's own checkbox upsells use the bar class as well, so they are left
    // out here and added below.
    const bars = [...bundle.querySelectorAll(EK_KACHING_BAR)].filter((bar) => !bar.matches(EK_KACHING_UPSELL));
    const bar =
      bars.find((item) => item.matches(EK_KACHING_SELECTED)) ||
      bars.find((item) => item.querySelector('input[type="radio"]:checked'));
    if (!bar) return null;

    const price = ekMoneyToCents(bar.querySelector(EK_KACHING_PRICE)?.textContent);
    if (price === null) return null;
    const full = ekMoneyToCents(bar.querySelector(EK_KACHING_FULL_PRICE)?.textContent);
    let total = price;
    let compareTotal = full !== null && full > price ? full : price;

    // Kaching upsells ticked in the deal ("Others also added"), as on The Complete Wallet.
    bundle.querySelectorAll(EK_KACHING_UPSELL).forEach((upsell) => {
      const ticked = upsell.matches(EK_KACHING_SELECTED) || upsell.querySelector('input[type="checkbox"]:checked');
      if (!ticked) return;
      const upsellPrice = ekMoneyToCents(upsell.querySelector(EK_KACHING_PRICE)?.textContent);
      if (upsellPrice === null) return;
      const upsellFull = ekMoneyToCents(upsell.querySelector(EK_KACHING_FULL_PRICE)?.textContent);
      total += upsellPrice;
      compareTotal += upsellFull !== null && upsellFull > upsellPrice ? upsellFull : upsellPrice;
    });

    return { price: total, compareAtPrice: compareTotal };
  }

  /**
   * The deal inside the same product section. The button's data-product-id can hold the variant
   * id, so the product id is only used as a fallback outside the section.
   */
  findKachingBundle() {
    const inSection = this.mainProduct?.querySelector(EK_KACHING_BUNDLE);
    if (inSection) return inSection;
    const productId = this.dataset.productId;
    if (!productId) return null;
    return document.querySelector(`${EK_KACHING_BUNDLE}[product-id="${productId}"]`);
  }

  /**
   * Waits for the Kaching deal to render, then keeps the button total in sync with the selected
   * bar. Kaching renders after the theme and rewrites the button on every bar change, so both
   * the deal and this element are observed.
   */
  watchKaching() {
    if (!this.dataset.price) return;
    const attach = () => {
      const bundle = this.findKachingBundle();
      if (!bundle) return false;
      this.kachingObserver = new MutationObserver(() => this.scheduleKachingTotal());
      this.kachingObserver.observe(bundle, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'checked'],
      });
      this.selfObserver = new MutationObserver(() => this.scheduleKachingTotal());
      this.selfObserver.observe(this, { subtree: true, childList: true, characterData: true });
      this.scheduleKachingTotal();
      return true;
    };
    if (attach()) return;
    let tries = 0;
    this.kachingPoll = setInterval(() => {
      tries += 1;
      if (attach() || tries > 40) clearInterval(this.kachingPoll);
    }, 500);
  }

  scheduleKachingTotal() {
    if (this.kachingTimer) return;
    // A short timeout rather than requestAnimationFrame, which does not run in a background tab.
    this.kachingTimer = setTimeout(() => {
      this.kachingTimer = null;
      this.applyKachingTotal();
    }, 30);
  }

  /**
   * Writes bar price plus add-ons into the button when a Kaching deal is visible. Only writes when
   * the text differs, so the observer on this element does not loop.
   */
  applyKachingTotal() {
    if (!this.hasPopularAddOns || !this.dataset.price) return;
    const base = this.getKachingBase();
    if (!base) return;

    const priceEl = this.querySelector('[data-current-price]');
    if (priceEl) {
      const text = formatMoney(base.price + this.addOnCents);
      if (priceEl.textContent !== text) priceEl.textContent = text;
    }

    const compareEl = this.querySelector('[data-current-compare-at-price]');
    if (compareEl && this.dataset.compareAtPrice) {
      const text = formatMoney(base.compareAtPrice + this.addOnCompareCents, window.SHOP_MONEY_FORMAT);
      if (compareEl.textContent !== text) compareEl.textContent = text;
    }
  }

  updatePriceWith(cents) {
    // Only update the price if the cents are set in the data attribute
    if (!this.dataset.price) return;

    this.addOnCents = Number(cents) || 0;
    const base = this.hasPopularAddOns ? this.getKachingBase() : null;
    const baseCents = base ? base.price : parseInt(this.dataset.price);

    this.querySelector('[data-current-price]').textContent = formatMoney(cents + baseCents);
  }

  /**
   * @method updateCompareAtPriceWith
   * @param {number} cents - The cents to update the compare price with.
   * @returns {void}
   */
  updateCompareAtPriceWith(cents) {
    this.addOnCompareCents = Number(cents) || 0;
    // Only update the price if the cents are set in the data attribute
    if (!this.dataset.compareAtPrice) return;
    const compareAtPrice = this.querySelector('[data-current-compare-at-price]');
    if (!compareAtPrice) return;
    const base = this.hasPopularAddOns ? this.getKachingBase() : null;
    const baseCents = base ? base.compareAtPrice : parseInt(this.dataset.compareAtPrice);
    compareAtPrice.textContent = formatMoney(cents + baseCents, window.SHOP_MONEY_FORMAT);
  }
}

customElements.define('product-price', ProductPrice);
